import { createFileRoute } from '@tanstack/react-router';
import { Calendar, Calculator, Mail, PlayCircle, Newspaper, MessageCircle, Download } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { OceanneLogo } from '@/components/ui/oceanne-logo';

// Inline brand SVGs (lucide-react v1 removed brand icons for trademark reasons)
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.62a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-1.05z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

const FB_URL = 'https://www.facebook.com/p/Océanne-Béatrice-Brodeur-61578880271035/';
const LI_URL = 'https://www.linkedin.com/in/océanne-brodeur-69130a240/';

export function LinktreePage() {
  const { lang } = useLang();
  const text = ta(t.links, lang);

  const secondaryButtons = [
    { Icon: Calendar, label: text.btn_audit, href: '/#contact' },
    { Icon: Calculator, label: text.btn_calc, href: '/calculatrice' },
    { Icon: Mail, label: text.btn_infolettre, href: '#' },
    { Icon: PlayCircle, label: text.btn_minicours, href: '#' },
    { Icon: Newspaper, label: text.btn_articles, href: '/carnet' },
    { Icon: MessageCircle, label: text.btn_contact, href: '/#contact' }
  ];

  return (
    <div className="min-h-[100dvh] bg-background py-8 px-5 text-foreground">
      <div className="mx-auto max-w-md space-y-6">
        {/* Phase 27.83 — leaf sprig logo Océanne validé (remplace monogramme "OB" text) */}
        <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-secondary/30 via-tertiary/20 to-primary/10">
          <OceanneLogo className="h-20 w-20 text-secondary" />
        </div>

        {/* Name + Subtitle */}
        <div className="text-center">
          <p className="font-display text-2xl text-foreground">Océanne Brodeur</p>
          <p className="mt-2 font-sans text-sm text-muted-foreground">{text.subtitle}</p>
        </div>

        {/* Tagline pill */}
        <div className="text-center">
          <span className="surface-peach-soft inline-block rounded-full px-4 py-2 font-sans text-sm italic text-foreground">
            {text.tagline}
          </span>
        </div>

        {/* Primary CTA — Phase 27.33-B disabled tant que PDF non fourni */}
        <a
          href="/guides/amg-quebec-2026.pdf"
          aria-disabled="true"
          onClick={(e) => e.preventDefault()}
          className="flex w-full items-center justify-center gap-3 rounded-md bg-primary/50 px-6 py-4 text-center font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground cursor-not-allowed"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          <span>{text.btn_guide}</span>
        </a>

        {/* 5 Secondary buttons */}
        <div className="space-y-3">
          {secondaryButtons.map((btn, i) => (
            <a
              key={i}
              href={btn.href}
              className="flex w-full items-center gap-4 rounded-md border border-border bg-background px-5 py-4 text-left font-sans text-sm text-foreground transition-colors hover:bg-muted"
            >
              <btn.Icon className="h-5 w-5 shrink-0 text-secondary" aria-hidden="true" />
              <span>{btn.label}</span>
            </a>
          ))}
        </div>

        {/* Social row */}
        <div className="flex items-center justify-center gap-4 pt-8">
          <a
            href={FB_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground transition-colors hover:bg-secondary/20"
          >
            <FacebookIcon className="h-4 w-4" />
          </a>
          {/* Phase 27.33-Medium — Codex audit #2 : Instagram/TikTok pas encore lancés.
              Liens disabled (aria-disabled + preventDefault) jusqu'à URLs officielles
              fournies par Océanne. Patron similaire au PDF guide pending. */}
          <a
            href="#"
            aria-label="Instagram (à venir)"
            aria-disabled="true"
            onClick={(e) => e.preventDefault()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground/60 cursor-not-allowed"
          >
            <InstagramIcon className="h-4 w-4" />
          </a>
          <a
            href="#"
            aria-label="TikTok (à venir)"
            aria-disabled="true"
            onClick={(e) => e.preventDefault()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground/60 cursor-not-allowed"
          >
            <TikTokIcon className="h-4 w-4" />
          </a>
          <a
            href={LI_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground transition-colors hover:bg-secondary/20"
          >
            <LinkedInIcon className="h-4 w-4" />
          </a>
        </div>

        {/* Mini footer */}
        <p className="pt-6 text-center font-sans text-xs text-muted-foreground">{text.footer_disclaimer}</p>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/links')({
  component: LinktreePage
});
